import { useInfiniteQuery, UseInfiniteQueryResult } from 'react-query';
import pDebounce from 'p-debounce';

import { getTokenWithType } from './useAuth';

import { getContactList } from '../utils/urls';

import { ContactList } from 'types/contacts';
import { ErrorModal, SortItemType } from 'types/modalTypes';

const pageLength = 50;

function getColumnFromId(id: string) {
  const idsList = [
    'Name',
    'Phone Number',
    'Used by Call Center',
    'Forwarded to ProtoCall',
    'Ok to give out number',
    'Notes',
  ];
  const index = idsList.findIndex(item => item === id);
  return `${index}`;
}

async function fetchContactList({
  requestData,
  accountId,
  pageParam,
}: TVariables): Promise<TResult> {
  requestData.append('start', `${pageParam}`);
  const response = await fetch(getContactList(accountId), {
    method: 'POST',
    headers: {
      Authorization: getTokenWithType(),
      'Content-Type': 'application/x-www-form-urlencoded',
    },
    body: requestData,
  });
  return response.json();
}

const debouncedFetch = pDebounce(fetchContactList, 500);

type TError = ErrorModal;

type TResult = {
  Data: Array<ContactList>;
  Draw: number;
  FilteredRecords: number;
  IsAutoRefreshEnabled: boolean;
  IsSuccess: boolean;
  PageNumber: number;
  StatusCode: number;
  TotalRecords: number;
};

type TVariables = {
  requestData: any;
  accountId: number;
  pageParam: number;
};

function formatRequestData(
  columnsSearch: Record<string, string>,
  sortOrder: string,
  keywordSearch: string,
  sortedColumn: string,
): FormData {
  const formData: any = new URLSearchParams();
  formData.append('draw', '1');
  formData.append('columns[0][data]', 'Name');
  formData.append('columns[0][name]', '');
  formData.append('columns[0][searchable]', 'true');
  formData.append('columns[0][orderable]', 'true');
  formData.append(
    'columns[0][search][value]',
    columnsSearch && columnsSearch['Name'] ? columnsSearch['Name'] : '',
  );
  formData.append('columns[0][search][regex]', 'false');
  formData.append('columns[1][data]', 'PhoneNumber');
  formData.append('columns[1][name]', '');
  formData.append('columns[1][searchable]', 'true');
  formData.append('columns[1][orderable]', 'true');
  formData.append(
    'columns[1][search][value]',
    columnsSearch && columnsSearch['Phone Number']
      ? columnsSearch['Phone Number']
      : '',
  );
  formData.append('columns[1][search][regex]', 'false');
  formData.append('columns[2][data]', 'IsUsedByCallCenter');
  formData.append('columns[2][name]', '');
  formData.append('columns[2][searchable]', 'true');
  formData.append('columns[2][orderable]', 'true');
  formData.append(
    'columns[2][search][value]',
    columnsSearch && columnsSearch['Used by Call Center']
      ? columnsSearch['Used by Call Center']
      : '',
  );
  formData.append('columns[2][search][regex]', 'false');
  formData.append('columns[3][data]', 'IsForwardedToProtoCall');
  formData.append('columns[3][name]', '');
  formData.append('columns[3][searchable]', 'true');
  formData.append('columns[3][orderable]', 'true');
  formData.append(
    'columns[3][search][value]',
    columnsSearch && columnsSearch['Forwarded to ProtoCall']
      ? columnsSearch['Forwarded to ProtoCall']
      : '',
  );
  formData.append('columns[3][search][regex]', 'false');
  formData.append('columns[4][data]', 'IsOkToGiveOutNumber');
  formData.append('columns[4][name]', '');
  formData.append('columns[4][searchable]', 'true');
  formData.append('columns[4][orderable]', 'true');
  formData.append(
    'columns[4][search][value]',
    columnsSearch && columnsSearch['Ok to give out number']
      ? columnsSearch['Ok to give out number']
      : '',
  );
  formData.append('columns[4][search][regex]', 'false');
  formData.append('columns[5][data]', 'Notes');
  formData.append('columns[5][name]', '');
  formData.append('columns[5][searchable]', 'true');
  formData.append('columns[5][orderable]', 'true');
  formData.append(
    'columns[5][search][value]',
    columnsSearch && columnsSearch['Notes'] ? columnsSearch['Notes'] : '',
  );
  formData.append('columns[5][search][regex]', 'false');
  formData.append('order[0][column]', sortedColumn);
  formData.append('order[0][dir]', sortOrder);
  formData.append('search[value]', `${keywordSearch}`);
  formData.append('search[regex]', 'false');
  formData.append('length', `${100}`);
  formData.append('timezoneOffset', '-330');
  return formData;
}

function useGetContactsList(
  accountId: number,
  columnsSearch: Record<string, string>,
  keywordSearch: string,
  sortItem: SortItemType,
): UseInfiniteQueryResult<TResult, TError> {
  const requestData = formatRequestData(
    columnsSearch,
    sortItem.sortOrder,
    keywordSearch,
    getColumnFromId(sortItem.sortKey),
  );
  return useInfiniteQuery(
    ['contact_list', keywordSearch, columnsSearch, sortItem],
    ({ pageParam = 0 }) =>
      debouncedFetch({ requestData, accountId, pageParam }),
    {
      getNextPageParam: (lastPage: TResult, allPages: Array<TResult>) => {
        if (lastPage !== null && lastPage?.Data?.length === pageLength)
          return allPages?.length * pageLength;
      },
      getPreviousPageParam: (firstPage: TResult, allPages: Array<TResult>) => {
        if (firstPage !== null && firstPage.Data?.length === pageLength)
          return allPages?.length - 1 * pageLength;
      },
      staleTime: 60 * 1000,
      refetchOnWindowFocus: false,
    },
  );
}

export { useGetContactsList };
